import type { TrustBoxItem } from '@hypercodestudio/basler-components';

import { buildUrlString } from '~/utils/buildUrlString';
import type { TrustBoxConfigItem } from '~/utils/contentful/enrichTrustBoxConfigurationSlugs';

export function useTrustBox() {
  const { $globalPageSettings, $activeLocale } = useNuxtApp();
  const { paymentIcons } = useBadges('trust');

  const isEnabled = computed(() => {
    const featureToggles = buildFeatureToggles($globalPageSettings.value);
    return Boolean(featureToggles?.useTrustBox);
  });

  const shopConfig = computed(
    () => ($globalPageSettings.value as Record<string, any>)?.shopConfig
  );

  const badge = computed<TrustBoxItem | undefined>(() => {
    if (!isEnabled.value) {
      return undefined;
    }

    const badgeData = shopConfig.value?.trustWarrantyBadge;
    if (!badgeData?.url) {
      return undefined;
    }

    return {
      image: badgeData.url,
      alt: badgeData.title
    };
  });

  const trustItems = computed<TrustBoxItem[]>(() => {
    if (!isEnabled.value) {
      return [];
    }

    const config = shopConfig.value?.trustBoxConfiguration as
      TrustBoxConfigItem[] | null;
    if (!config) {
      return [];
    }

    return config.map((item) => {
      const trustBoxItem: TrustBoxItem = {
        icon: item.icon,
        text: item.text
      };

      if (item.linkedSlug) {
        trustBoxItem.link = {
          uri: buildUrlString($activeLocale.value.webapp, item.linkedSlug),
          target: '_self',
          external: false
        };
      }

      return trustBoxItem;
    });
  });

  const serviceProviderItems = computed<TrustBoxItem[]>(() => {
    if (!isEnabled.value) {
      return [];
    }

    return paymentIcons.value.map((icon) => ({
      image: icon.src,
      alt: icon.alt
    }));
  });

  return {
    isEnabled,
    badge,
    trustItems,
    serviceProviderItems
  };
}
